// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button } from '@imprivata-cloud/design-system';
import { Pen } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { RiskLevelOption } from '@api/types';
import type { InstanceKey } from '../constants';
import './Conditions.less';
import { ConditionsDrawer } from './ConditionsDrawer';
import {
  actionLabelKey,
  CONDITION_LABEL_KEYS,
  EDIT_LINK_KEY,
  STATUS_LINE_KEYS,
  TEMPLATE_KEYS,
} from './labels';
import type { Condition, Rule, SummaryLine } from './types';
import { useRulesQuery } from './useRules';

export type StatusLine = keyof typeof STATUS_LINE_KEYS;

export const statusLine = ({
  hasRiskRule,
  riskAwarenessOn,
  riskAwarenessEnabled,
  conditionControlsDirty,
}: {
  hasRiskRule: boolean;
  riskAwarenessOn: boolean;
  riskAwarenessEnabled: boolean;
  conditionControlsDirty: boolean;
}): StatusLine | null => {
  if (conditionControlsDirty) {
    return 'save-first';
  }
  if (!riskAwarenessEnabled) {
    return null;
  }
  if (!riskAwarenessOn) {
    return hasRiskRule ? 'risk-awareness-off' : null;
  }
  if (!hasRiskRule) {
    return 'risk-not-applied';
  }
  return null;
};

export const summarise = (rules: Rule[]): SummaryLine[] =>
  rules.map(rule =>
    rule.kind === 'otherwise'
      ? { kind: 'otherwise', key: rule.key, action: rule.action }
      : {
          kind: 'conditional',
          key: rule.key,
          condition: rule.condition,
          conditionValue: rule.kind === 'tenant' ? rule.conditionValue : undefined,
          action: rule.action,
        },
  );

const RISK_CONDITIONS = {
  'risk-level': true,
  'role-is-administrator': false,
} satisfies Record<Condition, boolean>;

const isRiskCondition = (condition: Condition): boolean => RISK_CONDITIONS[condition];

const LINE = 'conditions-line';
const INERT_LINE = 'conditions-line conditions-inert';

const isInert = (line: SummaryLine, riskAwarenessOn: boolean, entitled: boolean): boolean =>
  line.kind !== 'otherwise' && isRiskCondition(line.condition) && !(riskAwarenessOn && entitled);

type ConditionsSummaryProps = {
  instanceKey: InstanceKey;
  riskAwarenessOn: boolean;
  riskAwarenessEnabled: boolean;
  conditionControlsDirty: boolean;
  entitled: boolean;
  riskLevels: RiskLevelOption[];
};

export const ConditionsSummary = ({
  instanceKey,
  riskAwarenessOn,
  riskAwarenessEnabled,
  conditionControlsDirty,
  entitled,
  riskLevels,
}: ConditionsSummaryProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const query = useRulesQuery(instanceKey, true);

  const rules = query.data?.decoded.rules ?? [];
  const status = statusLine({
    hasRiskRule: rules.some(rule => rule.kind !== 'otherwise' && isRiskCondition(rule.condition)),
    riskAwarenessOn,
    riskAwarenessEnabled,
    conditionControlsDirty,
  });

  return (
    <>
      <div className="conditions-summary">
        {summarise(rules).map(line => (
          <p className={isInert(line, riskAwarenessOn, entitled) ? INERT_LINE : LINE} key={line.key}>
            <SummaryLineText line={line} riskLevels={riskLevels} />
          </p>
        ))}
      </div>

      <div className="conditions-actions">
        {status !== null && <p className="conditions-status">{t(STATUS_LINE_KEYS[status])}</p>}

        <div className="conditions-edit-link">
          <Button
            variant="link"
            className="conditions-edit-link-btn"
            icon={<Pen size={16} />}
            onClick={() => setOpen(true)}
            disabled={conditionControlsDirty}
          >
            {t(EDIT_LINK_KEY)}
          </Button>
        </div>
      </div>

      <ConditionsDrawer instanceKey={instanceKey} open={open} onClose={() => setOpen(false)} entitled={entitled} riskAwarenessOn={riskAwarenessOn} riskLevels={riskLevels} />
    </>
  );
};

const SummaryLineText = ({
  line,
  riskLevels,
}: {
  line: SummaryLine;
  riskLevels: RiskLevelOption[];
}) => {
  const { t } = useTranslation();
  const key = actionLabelKey(line.action);
  const action = key === undefined ? '' : t(key);

  if (line.kind === 'otherwise') {
    return <Trans
      i18nKey={TEMPLATE_KEYS.otherwiseLine}
      values={{ action }}
      components={{ em: <span className="conditions-condition" /> }}
    />;
  }

  const condition =
    line.condition === 'risk-level'
      ? (riskLevels.find(level => level.token === line.conditionValue)?.label ??
        t(CONDITION_LABEL_KEYS[line.condition]))
      : t(CONDITION_LABEL_KEYS[line.condition]);

  return (
    <Trans
      i18nKey={TEMPLATE_KEYS.summaryLine}
      values={{ action, condition }}
      components={{ em: <span className="conditions-condition" /> }}
    />
  );
};
